import { getOcrProvider } from './providerAdapter.js';
import { canvasToBlob, extractPdfText, fileToCanvases, preprocessCanvas } from './utils/preprocess.js';

let tesseractLoader;

async function loadTesseract() {
  if (!tesseractLoader) {
    tesseractLoader = import(/* @vite-ignore */ 'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.0/dist/tesseract.esm.min.js');
  }
  return tesseractLoader;
}

export async function extractInvoiceFields(input, options = {}) {
  const provider = getOcrProvider();
  if (provider?.extractInvoiceFields) {
    return provider.extractInvoiceFields(input, options);
  }
  return runTesseractPipeline(input, options);
}

async function runTesseractPipeline(input, options) {
  const { onProgress, signal } = options;
  if (input instanceof File && input.type === 'application/pdf') {
    onProgress?.({ status: 'Leyendo texto nativo del PDF…', progress: 0.1 });
    const embeddedText = await extractPdfText(input);
    if (embeddedText.trim().length > 20 && /RAZ[ÓO]N\s+SOCIAL/i.test(embeddedText)) {
      const parsed = parseInvoiceText(embeddedText);
      onProgress?.({ status: 'Texto nativo del PDF analizado.', progress: 1 });
      return { fields: parsed.fields, confidences: parsed.confidences, text: embeddedText };
    }
  }
  const canvases = await prepareCanvases(input);
  const pages = [];
  for (let index = 0; index < canvases.length; index += 1) {
    if (signal?.aborted) throw new DOMException('OCR cancelado.', 'AbortError');
    onProgress?.({ status: `Procesando página ${index + 1} de ${canvases.length}…`, progress: index / canvases.length });
    const processed = preprocessCanvas(canvases[index]);
    const blob = await canvasToBlob(processed);
    const bitmap = await createImageBitmap(blob);
    const { data } = await recognize(bitmap, {
      signal,
      onProgress: message => onProgress?.({ ...message, progress: (index + (message.progress || 0)) / canvases.length }),
    });
    pages.push(data.text || '');
  }
  const text = pages.join('\n');
  const parsed = parseInvoiceText(text);
  return {
    fields: parsed.fields,
    confidences: parsed.confidences,
    text,
  };
}

async function prepareCanvases(input) {
  if (input instanceof HTMLCanvasElement) {
    return [input];
  }
  if (input instanceof ImageBitmap) {
    const canvas = document.createElement('canvas');
    canvas.width = input.width;
    canvas.height = input.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(input, 0, 0);
    return [canvas];
  }
  if (input instanceof File || input instanceof Blob) {
    const file = input instanceof File ? input : new File([input], 'document');
    return fileToCanvases(file);
  }
  throw new Error('Formato de entrada no soportado para OCR.');
}

async function recognize(imageBitmap, { onProgress, signal } = {}) {
  const module = await loadTesseract();
  const createWorker = module.createWorker || module.default?.createWorker;
  if (typeof createWorker !== 'function') {
    throw new Error('No se pudo iniciar el motor OCR. Reintentá en unos segundos.');
  }
  const worker = await createWorker('spa', 1, { logger: message => onProgress?.(message) });
  if (signal) {
    signal.addEventListener('abort', () => {
      worker.terminate();
    });
  }
  const result = await worker.recognize(imageBitmap);
  await worker.terminate();
  return result;
}

function parseInvoiceText(text) {
  const normalized = text.replace(/\s+/g, ' ');
  const upper = normalized.toUpperCase();
  const rawUpper = text.toUpperCase();

  const fields = {};
  const confidences = {};

  const cuitMatch = upper.match(/\b\d{2}-?\d{8}-?\d\b/);
  if (cuitMatch) {
    fields.cuit = formatCuit(cuitMatch[0]);
    confidences.cuit = 0.9;
  }

  const emailMatch = normalized.match(/(?:E-?MAIL|CORREO)\s*:?\s*([\w.+-]+@[\w.-]+\.[A-Z]{2,})/i);
  if (emailMatch) {
    fields.email = emailMatch[1];
    confidences.email = 0.85;
  }

  const phoneMatch = normalized.match(/TEL[ÉE]FONO\s*:?\s*([+\d][\d\s()-]{5,})/i);
  if (phoneMatch) {
    fields.telefono = phoneMatch[1].trim();
    confidences.telefono = 0.7;
  }

  if (/MONOTRIBUTO/i.test(normalized)) fields.condicionIva = 'MT';
  else if (/\bEXENTO\b/i.test(normalized)) fields.condicionIva = 'EX';
  else if (/RESPONSABLE\s+INSCRIPTO/i.test(normalized)) fields.condicionIva = 'RI';

  const caeMatch = upper.match(/CAE\s*:?\s*(\d{8,})/);
  if (caeMatch) {
    fields.cae = caeMatch[1];
    confidences.cae = 0.8;
  }

  const caeVtoMatch = upper.match(/(?:VENCIMIENTO|FECHA\s+DE\s+VTO\.?\s+DE)\s*CAE\s*:?\s*(\d{2}[/-]\d{2}[/-]\d{2,4})/);
  if (caeVtoMatch) {
    fields.caeVto = normalizeDate(caeVtoMatch[1]);
    confidences.caeVto = 0.6;
  }

  const dateMatch = upper.match(/(\d{2}[/-]\d{2}[/-]\d{4})/);
  if (dateMatch) {
    fields.fecha = normalizeDate(dateMatch[1]);
    confidences.fecha = 0.65;
  }

  const typeMatch = upper.match(/FACTURA\s+[ABC]|FACTURA\s+M|NOTA\s+DE\s+CR[EÉ]DITO|NOTA\s+DE\s+D[EÉ]BITO/);
  if (typeMatch) {
    fields.tipo = capitalize(typeMatch[0]);
    confidences.tipo = 0.7;
  }

  const pvMatch = upper.match(/(?:PTO\.?\s*VTA\.?\s*|N[°ºO]\s*)(\d{4,5})\s*-/);
  if (pvMatch) {
    fields.puntoVenta = pvMatch[1];
    confidences.puntoVenta = 0.75;
  }

  const numberMatch = upper.match(/N[ÚU]MERO\s*(\d{8})/);
  if (numberMatch) {
    fields.numero = numberMatch[1];
    confidences.numero = 0.7;
  }

  const invoiceNumberMatch = upper.match(/N[°ºO]\s*\d{4,5}\s*-\s*(\d{4,12})/);
  if (invoiceNumberMatch && !fields.numero) {
    fields.numero = invoiceNumberMatch[1];
    confidences.numero = 0.85;
  }

  const netoMatch = upper.match(/NETO\s*(?:GRAVADO)?\s*:?\s*\$?\s*([0-9.,]+)/);
  if (netoMatch) {
    fields.neto = toAmount(netoMatch[1]);
    confidences.neto = 0.7;
  }

  const subtotalGravadoMatch = upper.match(/SUBTOTAL\s+GRAVADO\s*:?\s*\$?\s*([0-9.,]+)/);
  if (subtotalGravadoMatch && !fields.neto) {
    fields.neto = toAmount(subtotalGravadoMatch[1]);
    confidences.neto = 0.9;
  }

  const iva21Match = upper.match(/IVA\s*21%\s*:?\s*([0-9.,]+)/);
  if (iva21Match) {
    fields.iva21 = toAmount(iva21Match[1]);
    confidences.iva21 = 0.7;
  }

  const iva105Match = upper.match(/IVA\s*10[,.]5%\s*:?\s*([0-9.,]+)/);
  if (iva105Match) {
    fields.iva105 = toAmount(iva105Match[1]);
    confidences.iva105 = 0.65;
  }

  const iva27Match = upper.match(/IVA\s*27%\s*:?\s*([0-9.,]+)/);
  if (iva27Match) {
    fields.iva27 = toAmount(iva27Match[1]);
    confidences.iva27 = 0.6;
  }

  const exentoMatch = upper.match(/EXENTO\s*:?\s*([0-9.,]+)/);
  if (exentoMatch) {
    fields.exento = toAmount(exentoMatch[1]);
    confidences.exento = 0.5;
  }

  const totalMatch = upper.match(/TOTAL\s*(?:GENERAL|A\s*PAGAR)?\s*:?\s*\$?\s*([0-9.,]+)/);
  if (totalMatch) {
    fields.total = toAmount(totalMatch[1]);
    confidences.total = 0.85;
  }

  const supplierMatch = upper.match(/RAZ[ÓO]N\s+SOCIAL\s*:?\s*([^\n]+)/);
  if (supplierMatch) {
    fields.proveedor = cleanupText(supplierMatch[1]);
    confidences.proveedor = 0.55;
  } else if (cuitMatch) {
    const index = upper.indexOf(cuitMatch[0]);
    if (index > 0) {
      const preceding = normalized.slice(Math.max(0, index - 60), index).trim();
      if (preceding) {
        fields.proveedor = cleanupText(preceding);
        confidences.proveedor = 0.4;
      }
    }
  }

  const supplierNativeMatch = rawUpper.match(/RAZ[ÓO]N\s+SOCIAL\s*:?\s*\r?\n?\s*([^\r\n]+)/);
  if (supplierNativeMatch) {
    fields.proveedor = cleanupText(supplierNativeMatch[1]);
    confidences.proveedor = 0.92;
  }

  const supplierBoundedMatch = normalized.match(/RAZ[ÓO]N\s+SOCIAL\s*:?\s*(.+?)(?=\s+(?:INFORMACION|INFORMACIÓN|CONDICIONES|CLIENTE|CUIT)\b)/i);
  if (supplierBoundedMatch) {
    fields.proveedor = cleanupText(supplierBoundedMatch[1]);
    confidences.proveedor = 0.92;
  }

  const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const addressIndex = lines.findIndex(line => /^(DOMICILIO|DIRECCI[ÓO]N)\b/i.test(line));
  if (addressIndex > 0) {
    const headerName = lines
      .slice(Math.max(0, addressIndex - 4), addressIndex)
      .reverse()
      .find(line => /[A-ZÁÉÍÓÚÑ]{3}/i.test(line) && !/(FACTURA|COD|IVA|CAE|CUIT|SISTEMA)/i.test(line));
    if (headerName) {
      fields.proveedor = cleanupText(headerName);
      confidences.proveedor = 0.8;
    }
  }

  const caeLine = upper.match(/CAE\s*:?\s*\d{8,}\s*VENCIMIENTO\s*CAE\s*:?\s*(\d{2}[/-]\d{2}[/-]\d{2,4})/);
  if (caeLine && !fields.caeVto) {
    fields.caeVto = normalizeDate(caeLine[1]);
    confidences.caeVto = 0.6;
  }

  return { fields, confidences };
}

function capitalize(value) {
  return value
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function toAmount(value) {
  if (!value) return 0;
  const normalized = value.replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
}

function normalizeDate(value) {
  if (!value) return '';
  const parts = value.replace(/[^0-9]/g, ' ').trim().split(' ').filter(Boolean);
  if (parts.length === 3) {
    const [day, month, year] = parts;
    const normalizedYear = year.length === 2 ? `20${year}` : year;
    return `${normalizedYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return value;
}

function cleanupText(value) {
  return value.replace(/[^A-Z0-9\s.-]/gi, ' ').trim();
}

function formatCuit(value) {
  const digits = value.replace(/[^\d]/g, '').slice(0, 11);
  return digits.replace(/(\d{2})(\d{8})(\d{1})/, '$1-$2-$3');
}
